import React, {
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from "react";
import PropTypes from "prop-types";
import userService from "../../../services/userService";
import { resolveUserDisplayName, resolveUserUsername } from "../../../utils/userIdentity";

import "./index.scss";

const normalizeUserId = (candidate) => {
    if (typeof candidate !== "string") {
        return null;
    }
    const trimmed = candidate.trim();
    return trimmed.length ? trimmed : null;
};

const toKey = (candidate) => {
    const normalized = normalizeUserId(candidate);
    return normalized == null ? null : String(normalized);
};

const profileFromMember = (member) => {
    if (!member || typeof member !== "object") {
        return null;
    }
    const displayName = resolveUserDisplayName(member);
    const username = resolveUserUsername(member);
    if (!displayName && !username) {
        return null;
    }
    return {
        displayName,
        username,
    };
};

const formatDisplayName = (profile, fallbackId) => {
    if (!profile || typeof profile !== "object") {
        return fallbackId ? `User #${fallbackId}` : "Unknown user";
    }
    return resolveUserDisplayName(profile, fallbackId ? `User #${fallbackId}` : "Unknown user");
};

const buildSearchLabel = (label, role, status) => {
    const parts = [label];
    if (role) {
        parts.push(role);
    }
    if (status) {
        parts.push(status);
    }
    return parts.join(" ").toLowerCase();
};

const filterActiveMembers = (members = []) => (
    members.filter((member) => member && member.userId != null && member.status === "ACTIVE")
);

const EventParticipantsPicker = ({
    id,
    ariaLabelledby,
    ariaDescribedby,
    members = [],
    value = [],
    onChange,
    disabled = false,
    currentUserId,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const directoryRef = useRef(new Map());
    const [directoryVersion, setDirectoryVersion] = useState(0);
    const triggerRef = useRef(null);
    const dialogRef = useRef(null);
    const autoId = useId();
    const controlId = id || `event-participants-${autoId.replace(/:/g, "")}`;
    const panelId = `${controlId}-panel`;

    const normalizedValue = useMemo(() => (
        Array.from(
            new Set(
                (Array.isArray(value) ? value : [])
                    .map((candidate) => normalizeUserId(candidate))
                    .filter((candidate) => candidate != null)
            )
        )
    ), [value]);

    const selectedKeySet = useMemo(() => (
        new Set(normalizedValue.map((candidate) => String(candidate)))
    ), [normalizedValue]);

    const directorySnapshot = useMemo(() => ({
        version: directoryVersion,
        map: new Map(directoryRef.current),
    }), [directoryVersion]);
    const directoryMap = directorySnapshot.map;

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }
        const handlePointerDown = (event) => {
            if (!dialogRef.current || !triggerRef.current) {
                return;
            }
            const withinDialog = dialogRef.current.contains(event.target);
            const withinTrigger = triggerRef.current.contains(event.target);
            if (!withinDialog && !withinTrigger) {
                setIsOpen(false);
            }
        };
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                event.preventDefault();
                event.stopPropagation();
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handlePointerDown);
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && dialogRef.current) {
            dialogRef.current.focus({ preventScroll: true });
        }
    }, [isOpen]);

    useEffect(() => {
        const activeMembers = filterActiveMembers(members);
        let changed = false;
        activeMembers.forEach((member) => {
            const key = toKey(member?.userId);
            const profile = profileFromMember(member);
            if (!key || !profile) {
                return;
            }
            const previous = directoryRef.current.get(key) || {};
            const next = {
                ...previous,
                ...profile,
            };
            if (
                previous.displayName !== next.displayName
                || previous.username !== next.username
            ) {
                directoryRef.current.set(key, next);
                changed = true;
            }
        });
        if (changed) {
            setDirectoryVersion((current) => current + 1);
        }
    }, [members]);

    useEffect(() => {
        const activeMembers = filterActiveMembers(members);
        const candidateIds = new Set(
            [
                ...activeMembers.map((member) => member.userId),
                ...normalizedValue,
            ].map((candidate) => normalizeUserId(candidate))
                .filter((candidate) => candidate != null)
        );
        const missing = Array.from(candidateIds)
            .map((candidate) => toKey(candidate))
            .filter((key) => key != null && !directoryRef.current.has(key));
        if (!missing.length) {
            return undefined;
        }
        let isCancelled = false;
        (async () => {
            const results = await Promise.all(
                missing.map(async (key) => {
                    try {
                        const profile = await userService.fetchUserById(key);
                        return profile;
                    } catch (error) {
                        return null;
                    }
                }),
            );
            if (isCancelled) {
                return;
            }
            let changed = false;
            results.forEach((profile, index) => {
                const userIdKey = missing[index];
                if (!directoryRef.current.has(userIdKey)) {
                    directoryRef.current.set(userIdKey, profile || {});
                    changed = true;
                }
            });
            if (changed) {
                setDirectoryVersion((current) => current + 1);
            }
        })();
        return () => {
            isCancelled = true;
        };
    }, [members, normalizedValue]);

    const formattedMembers = useMemo(() => {
        const activeMembers = filterActiveMembers(members);
        return activeMembers.map((member) => {
            const key = toKey(member.userId);
            const profile = key ? directoryMap.get(key) : null;
            const label = formatDisplayName(profile, member.userId);
            const isSelf = currentUserId != null && String(member.userId) === String(currentUserId);
            return {
                userId: member.userId,
                label: isSelf ? `${label} (You)` : label,
                rawLabel: label,
                role: member.role,
                status: member.status,
                key,
                searchLabel: buildSearchLabel(label, member.role, member.status),
            };
        }).sort((a, b) => a.rawLabel.localeCompare(b.rawLabel));
    }, [members, currentUserId, directoryMap]);

    const filteredMembers = useMemo(() => {
        const normalized = searchTerm.trim().toLowerCase();
        if (!normalized) {
            return formattedMembers;
        }
        return formattedMembers.filter((member) => member.searchLabel.includes(normalized));
    }, [formattedMembers, searchTerm]);

    const selectedParticipants = useMemo(() => (
        normalizedValue.map((candidate) => {
            const key = toKey(candidate);
            const profile = key ? directoryMap.get(key) : null;
            const rawLabel = formatDisplayName(profile, candidate);
            const isSelf = currentUserId != null && key === String(currentUserId);
            return {
                userId: candidate,
                label: isSelf ? `${rawLabel} (You)` : rawLabel,
                key,
            };
        })
    ), [normalizedValue, currentUserId, directoryMap]);

    const handleToggleOpen = useCallback(() => {
        if (disabled) {
            return;
        }
        setIsOpen((current) => !current);
    }, [disabled]);

    const handleControlKeyDown = useCallback((event) => {
        if (disabled) {
            return;
        }
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setIsOpen((current) => !current);
        }
    }, [disabled]);

    const handleClose = useCallback(() => {
        setIsOpen(false);
    }, []);

    const emitChange = useCallback((nextValue) => {
        const normalized = Array.from(new Set(
            nextValue
                .map((candidate) => normalizeUserId(candidate))
                .filter((candidate) => candidate != null)
        ));
        onChange?.(normalized);
    }, [onChange]);

    const handleToggleParticipant = useCallback((userId) => {
        if (disabled || userId == null) {
            return;
        }
        const key = toKey(userId);
        if (!key) {
            return;
        }
        const exists = normalizedValue.some((candidate) => String(candidate) === key);
        if (exists) {
            emitChange(normalizedValue.filter((candidate) => String(candidate) !== key));
        } else {
            emitChange([...normalizedValue, normalizeUserId(userId)]);
        }
    }, [disabled, emitChange, normalizedValue]);

    const handleRemoveChip = useCallback((userId) => {
        const key = toKey(userId);
        if (!key) {
            return;
        }
        emitChange(normalizedValue.filter((candidate) => String(candidate) !== key));
    }, [emitChange, normalizedValue]);

    const handleSearchChange = useCallback((event) => {
        setSearchTerm(event.target.value);
    }, []);

    const focusTrigger = useCallback(() => {
        if (!triggerRef.current) {
            return;
        }
        triggerRef.current.focus();
    }, []);

    const handleSrToggle = useCallback(() => {
        if (disabled) {
            return;
        }
        handleToggleOpen();
        setTimeout(focusTrigger, 0);
    }, [disabled, handleToggleOpen, focusTrigger]);

    return (
        <div className="event-participants">
            <button
                type="button"
                id={controlId}
                className="event-participants__sr-trigger"
                onClick={handleSrToggle}
                disabled={disabled}
                aria-hidden="true"
                tabIndex={-1}
            >
                Toggle participants
            </button>
            <div
                className={`event-participants__control${disabled ? " event-participants__control--disabled" : ""}`}
                aria-haspopup="dialog"
                aria-expanded={isOpen}
                aria-labelledby={ariaLabelledby}
                aria-describedby={ariaDescribedby}
                aria-controls={isOpen ? panelId : undefined}
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-disabled={disabled || undefined}
                onClick={handleToggleOpen}
                onKeyDown={handleControlKeyDown}
                ref={triggerRef}
            >
                <div className="event-participants__selection">
                    {selectedParticipants.length > 0 ? (
                        selectedParticipants.map((participant) => (
                            <span key={participant.key} className="event-participants__chip">
                                <span className="event-participants__chip-avatar" aria-hidden="true">
                                    {participant.label.charAt(0).toUpperCase()}
                                </span>
                                <span className="event-participants__chip-label">
                                    {participant.label}
                                </span>
                                {!disabled && (
                                    <button
                                        type="button"
                                        className="event-participants__chip-remove"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            handleRemoveChip(participant.userId);
                                        }}
                                        aria-label={`Remove ${participant.label}`}
                                    >
                                        ×
                                    </button>
                                )}
                            </span>
                        ))
                    ) : (
                        <span className="event-participants__placeholder">Add participants</span>
                    )}
                </div>
                <span className="event-participants__chevron" aria-hidden="true">▾</span>
            </div>
            {isOpen && (
                <div
                    role="dialog"
                    aria-modal="true"
                    className="event-participants__panel"
                    ref={dialogRef}
                    tabIndex={-1}
                    id={panelId}
                >
                    <div className="event-participants__panel-header">
                        <div>
                            <h3>Invite teammates</h3>
                            <p>Select people from this workspace to join the event.</p>
                        </div>
                        <button
                            type="button"
                            className="event-participants__close"
                            onClick={handleClose}
                            aria-label="Close participant selector"
                        >
                            ×
                        </button>
                    </div>
                    <div className="event-participants__search">
                        <input
                            type="search"
                            value={searchTerm}
                            onChange={handleSearchChange}
                            placeholder="Search members by name or role"
                            autoFocus
                        />
                    </div>
                    <ul className="event-participants__list">
                        {filteredMembers.length === 0 && (
                            <li className="event-participants__empty">No matching members.</li>
                        )}
                        {filteredMembers.map((member) => {
                            const isSelected = member.key != null && selectedKeySet.has(member.key);
                            return (
                                <li key={member.key} className="event-participants__item">
                                    <button
                                        type="button"
                                        className={`event-participants__row${isSelected ? " is-selected" : ""}`}
                                        onClick={() => handleToggleParticipant(member.userId)}
                                    >
                                        <span className="event-participants__row-avatar" aria-hidden="true">
                                            {member.rawLabel.charAt(0).toUpperCase()}
                                        </span>
                                        <span className="event-participants__row-body">
                                            <span className="event-participants__row-label">{member.label}</span>
                                            <span className="event-participants__row-meta">
                                                {member.role}
                                            </span>
                                        </span>
                                        <span className="event-participants__row-indicator" aria-hidden="true">
                                            {isSelected ? "✓" : "+"}
                                        </span>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                    <footer className="event-participants__footer">
                        <span>{selectedParticipants.length} selected</span>
                        <button
                            type="button"
                            className="event-participants__done"
                            onClick={handleClose}
                        >
                            Done
                        </button>
                    </footer>
                </div>
            )}
        </div>
    );
};

EventParticipantsPicker.propTypes = {
    id: PropTypes.string,
    ariaLabelledby: PropTypes.string,
    ariaDescribedby: PropTypes.string,
    members: PropTypes.arrayOf(PropTypes.shape({
        membershipId: PropTypes.string,
        userId: PropTypes.string,
        role: PropTypes.string,
        status: PropTypes.string,
        displayName: PropTypes.string,
        username: PropTypes.string,
        user: PropTypes.object,
        profile: PropTypes.object,
    })),
    value: PropTypes.arrayOf(PropTypes.string),
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    currentUserId: PropTypes.string,
};

export default EventParticipantsPicker;
